/**
 * Locale-aware Zod error map for the kiosk app (#461).
 *
 * Mirrors apps/web (#460) and apps/admin (#462): translates the common Zod issue
 * codes into the active locale so schemas that don't carry a custom message —
 * including schemas imported from `@autopia/api-contracts` and fed straight into
 * `zodResolver` — surface localized text.
 *
 * Kiosk has no next-intl; it reads messages directly from the typed
 * `translations` object (`translations[locale].validation`), so no
 * `createTranslator` is needed. The returned map is instance-agnostic (it only
 * reads issue codes and returns `{ message }`), so the same value is installed
 * on both the app's zod instance and the api-contracts CJS instance — see the
 * install site in App.tsx.
 */

import { z } from 'zod';

import { translations, type Locale } from './translations';

export function makeZodErrorMap(locale: Locale): z.ZodErrorMap {
  const v = translations[locale].validation;

  return (issue, ctx) => {
    switch (issue.code) {
      case z.ZodIssueCode.invalid_type:
        return { message: issue.received === 'undefined' ? v.required : v.invalidType };
      case z.ZodIssueCode.too_small:
        return {
          message: issue.type === 'string' && issue.minimum === 1 ? v.required : v.tooSmall,
        };
      case z.ZodIssueCode.too_big:
        return { message: v.tooBig };
      case z.ZodIssueCode.invalid_string:
        return { message: issue.validation === 'email' ? v.email : v.invalidFormat };
      default:
        return { message: ctx.defaultError };
    }
  };
}
